import { useCallback, useEffect, useState } from 'react'
import { Check, Copy, Link2, Loader2, Trash2 } from 'lucide-react'
import type { CfoShareLink, ServerApiClient } from '../../../../data/ServerApiClient'

// External "anyone with the link" sharing for a board pack. Mints revocable
// view-only token links (optionally pinned to a published month, optionally
// expiring) that render the frozen snapshot at /share/cfo/:token without a
// FuseGrid account. Sits alongside the internal user/group CfoSharingPanel.

type CfoShareLinksPanelProps = {
  appApi: ServerApiClient
  workbookId: string
  captureRevision: () => Promise<string>
}

function shareUrlFor(token: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  return `${origin}/share/cfo/${token}`
}

export function CfoShareLinksPanel({ appApi, workbookId, captureRevision }: CfoShareLinksPanelProps) {
  const [links, setLinks] = useState<CfoShareLink[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')
  const [label, setLabel] = useState('')
  const [expiry, setExpiry] = useState('')
  const [freshUrl, setFreshUrl] = useState('')
  const [copied, setCopied] = useState('')

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const res = await appApi.listCfoReportShareLinks(workbookId)
      setLinks(Array.isArray(res) ? res : [])
      setError('')
    } catch (err: any) {
      setError(String(err?.message || err) || 'Could not load share links.')
    } finally {
      setLoading(false)
    }
  }, [appApi, workbookId])

  useEffect(() => {
    void reload()
  }, [reload])

  const createLink = useCallback(async () => {
    setCreating(true)
    setError('')
    try {
      const req: { revision_id: string; label?: string | null; expires_at?: string | null } = { revision_id: await captureRevision() }
      if (label.trim()) req.label = label.trim()
      if (expiry) req.expires_at = new Date(expiry).toISOString()
      const res = await appApi.createCfoReportShareLink(workbookId, req)
      setFreshUrl(shareUrlFor(res.token))
      setLabel('')
      setExpiry('')
      await reload()
    } catch (err: any) {
      setError(String(err?.message || err) || 'Could not create the link.')
    } finally {
      setCreating(false)
    }
  }, [appApi, captureRevision, expiry, label, reload, workbookId])

  const revoke = useCallback(
    async (linkId: string) => {
      try {
        await appApi.revokeCfoReportShareLink(workbookId, linkId)
        await reload()
      } catch (err: any) {
        setError(String(err?.message || err) || 'Could not revoke the link.')
      }
    },
    [appApi, reload, workbookId],
  )

  const copy = useCallback(async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(key)
      window.setTimeout(() => setCopied(''), 1800)
    } catch {
      /* clipboard blocked — the input is selectable as a fallback */
    }
  }, [])

  return (
    <div className="cfo-sharelinks" data-testid="cfo-presentation-share-links">
      <div className="cfo-sharelinks-head">
        <Link2 size={15} />
        <div>
          <strong>Anyone with the link</strong>
          <span>Private, revocable, view-only — no FuseGrid account needed.</span>
        </div>
      </div>

      <div className="cfo-sharelinks-create">
        <div className="cfo-sharelinks-revision-note"><strong>Exact displayed version</strong><span>A frozen revision is captured when you create the link.</span></div>
        <label>
          Label (optional)
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Board – May" />
        </label>
        <label>
          Expires (optional)
          <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
        </label>
        <button type="button" className="cfo-sharelinks-create-btn" onClick={() => { void createLink() }} disabled={creating} data-testid="cfo-share-link-create">
          {creating ? <Loader2 size={14} className="spin" /> : <Link2 size={14} />}
          {creating ? 'Creating…' : 'Create link'}
        </button>
      </div>

      {freshUrl ? (
        <div className="cfo-sharelinks-fresh" data-testid="cfo-share-link-fresh">
          <input readOnly value={freshUrl} onFocus={(e) => e.currentTarget.select()} />
          <button type="button" onClick={() => { void copy(freshUrl, 'fresh') }}>
            {copied === 'fresh' ? <Check size={14} /> : <Copy size={14} />} {copied === 'fresh' ? 'Copied' : 'Copy'}
          </button>
        </div>
      ) : null}

      {error ? <div className="cfo-shell-notice error">{error}</div> : null}

      <ul className="cfo-sharelinks-list">
        {loading ? (
          <li className="cfo-sharelinks-empty">Loading links…</li>
        ) : links.length === 0 ? (
          <li className="cfo-sharelinks-empty">No links yet.</li>
        ) : (
          links.map((link) => {
            const url = `${typeof window !== 'undefined' ? window.location.origin : ''}/share/cfo/…`
            return (
              <li key={link.id} className="cfo-sharelinks-row" data-status={link.status}>
                <div className="cfo-sharelinks-meta">
                  <strong>{link.label || link.revision_label || (link.period ? `Report ${link.period}` : 'Frozen report')}</strong>
                  <span>
                    <em data-status={link.status}>{link.status}</em>
                    {` · ${link.view_count} view${link.view_count === 1 ? '' : 's'}`}
                    {link.expires_at ? ` · expires ${new Date(link.expires_at).toLocaleDateString()}` : ''}
                  </span>
                </div>
                <div className="cfo-sharelinks-actions">
                  <span className="cfo-sharelinks-url" title={url}>{url}</span>
                  {link.status === 'active' ? (
                    <button type="button" onClick={() => { void revoke(link.id) }} title="Revoke link" aria-label="Revoke link" data-testid={`cfo-share-link-revoke-${link.id}`}>
                      <Trash2 size={14} />
                    </button>
                  ) : null}
                </div>
              </li>
            )
          })
        )}
      </ul>
    </div>
  )
}

export default CfoShareLinksPanel
